import { clone } from 'helpers/global.helper/global.helper'
import Datetime from 'utils/datetime/datetime'
import { aggregateDataset } from './dataset.helper'

/**
 * Calculates and returns a new dataset based on the provided datasets and calculation instructions.
 *
 * This function supports nested calculation steps, element-wise operations, accumulation, and
 * optional aggregation or absolute value transformations as specified in the `CalcDatasetInstruction`.
 * If the instruction contains multiple calculation steps, it processes each step recursively,
 * appending the result to the datasets array and returning the result of the last step.
 *
 * @param datasets - The array of input datasets to use for calculations.
 * @param datasetInstruction - The calculation instruction specifying how to process the datasets.
 * @returns The calculated dataset as specified by the instruction.
 */
export function getCalculatedDataset(datasets: Dataset[], datasetInstruction: CalcDatasetInstruction): Dataset {
  const contract = datasetInstruction.contract as DatasetContractCalc
  const operator = contract.operator ?? `+`
  const accumulate = contract.accumulate
  const elementWise = contract.element_wise ?? !!accumulate
  const ifNan = contract.if_nan
  const arithmeticOperators = new Set([`+`, `-`, `*`, `/`])
  const nullAsZero = (contract.null_as_zero ?? false) && arithmeticOperators.has(operator)

  // Nested calc-steps for more complex (but still simple) calculations
  if (contract?.steps?.length && contract.steps.length > 0) {
    for (let i = 0; i < contract.steps.length; i++) {
      const step = contract.steps[i]
      const stepDSInstruction = clone(datasetInstruction)
      stepDSInstruction.return_id = step.return_id
      stepDSInstruction.contract = {
        ...stepDSInstruction.contract,
        steps: undefined,
        ...step,
      }

      const stepDataset = getCalculatedDataset(datasets, stepDSInstruction)
      datasets.push(stepDataset)
    }

    // Steps-calc-datasets always returns dataset from the last step
    const lastDataset = datasets[datasets.length - 1]
    if (datasetInstruction.return_id) {
      lastDataset.return_id = datasetInstruction.return_id
    }
    return lastDataset
  }

  const datasetWithShortestValueLength = getDatasetWithShortestValueLengthInDatasetInstruction(
    datasets,
    datasetInstruction
  )
  const nrOfEntries = elementWise ? (datasetWithShortestValueLength?.values?.length ?? 0) : 1
  let times = datasetWithShortestValueLength?.times?.map((time) => time) ?? []
  let quantity = 0
  let accumulatedValue = 0

  if (!elementWise && times.length) {
    times = [times[times.length - 1]]
  }

  const datasetsByReturnId = elementWise
    ? getDatasetsByReturnId(datasets)
    : getAggregatedDatasetsByReturnId(datasets, datasetInstruction.filter?.aggregate || `sum`)

  let dataset: Dataset = {
    return_id: datasetInstruction.return_id,
    times: times as ISODateTime[] | number[],
    values: [],
  }

  for (let i = 0; i < nrOfEntries; i++) {
    let baseValue = null
    let isFirstEntry = true

    for (const returnId of contract.variables) {
      let value =
        typeof returnId === `number`
          ? returnId
          : datasetsByReturnId[returnId]
            ? datasetsByReturnId[returnId].values[i]
            : 0 //Datasets that does not exists in a calc will be 0

      // When null_as_zero is enabled, treat null/undefined values as 0
      if (nullAsZero && (value === null || value === undefined)) {
        value = 0
      }

      if (isFirstEntry) {
        baseValue = value
        isFirstEntry = false
        continue
      }

      if (value === null || value === undefined || typeof value !== `number`) {
        if (operator === `if`) {
          baseValue = null
        }
        continue
      }

      if (baseValue === null && operator === `+`) {
        baseValue = value
        continue
      }

      // When null_as_zero is enabled, coerce null baseValue to 0 for arithmetic operators
      if (nullAsZero && baseValue === null) {
        baseValue = 0
      }

      if (operator === 'ifnot') {
        if (baseValue === null) {
          baseValue = value
        }
      }

      if (baseValue === null) {
        continue
      }
      if (operator === `+`) {
        baseValue += value
      } else if (operator === `-`) {
        baseValue -= value
      } else if (operator === `/`) {
        baseValue /= value
      } else if (operator === `*`) {
        baseValue *= value
      } else if (operator === `min`) {
        baseValue = Math.min(baseValue, value)
      } else if (operator === `max`) {
        baseValue = Math.max(baseValue, value)
      } else if (operator === `if`) {
        if (!value) {
          baseValue = 0
        } else {
          baseValue = value
        }
      }
    }

    if (accumulate && baseValue !== null) {
      quantity++
      accumulatedValue += baseValue

      if (accumulate === `mean`) {
        baseValue = accumulatedValue / quantity
      } else if (accumulate === `sum`) {
        baseValue = accumulatedValue
      }
    }

    // Fallback to value of ifNan if the base value is NaN or Infinity
    if (ifNan !== undefined && baseValue !== null) {
      if (isNaN(baseValue) || !isFinite(baseValue)) {
        baseValue = ifNan
      }
    }

    dataset.values.push(baseValue)
  }

  if (elementWise && datasetInstruction.filter?.aggregate && !datasetInstruction.filter.aggregate_in_frontend) {
    dataset = aggregateDataset(clone(dataset), datasetInstruction.filter.aggregate) as Dataset
  }

  if (datasetInstruction.filter?.absolute_values) {
    dataset = setAbsoluteValues(dataset)
  }

  return dataset
}

export function calculateDatasetsWithSameReturnId(datasets: Dataset[], operator: string): Dataset[] {
  const datasetMap: Record<string, Dataset> = {}

  datasets.forEach((dataset) => {
    const returnId = dataset.return_id

    if (!datasetMap[returnId]) {
      datasetMap[returnId] = dataset
    } else {
      const savedDataset = datasetMap[returnId]
      const savedDatasetTimes = new Set<ISODateTime>(savedDataset.times as ISODateTime[])
      const unsavedDatasetTimes = new Set<ISODateTime>(dataset.times as ISODateTime[])

      const hasOverlap = [...savedDatasetTimes].some((time) => unsavedDatasetTimes.has(time))

      if (!hasOverlap) {
        datasetMap[returnId].values = [...savedDataset.values, ...dataset.values]
        datasetMap[returnId].times = [...savedDataset.times, ...dataset.times] as ISODateTime[]
      } else {
        const joinedTimes = [...savedDataset.times, ...dataset.times] as ISODateTime[]
        const allTimes = Datetime.sortDatetimes(joinedTimes)
        const storedValueMap: Record<ISODateTime, number | null | undefined> = savedDataset.values.reduce(
          (acc, v, i) => ({
            ...acc,
            [savedDataset.times[i]]: v,
          }),
          {}
        )
        const valueMap: Record<ISODateTime, number | null | undefined> = dataset.values.reduce(
          (acc, v, i) => ({
            ...acc,
            [dataset.times[i]]: v,
          }),
          {}
        )

        const values = allTimes.map((t) => {
          const savedValue = storedValueMap[t]
          const unsavedValue = valueMap[t]

          if (savedValue == null || unsavedValue == null) {
            return null
          }

          // Keep operand order from input; first seen dataset is always on left.
          return applyOperator(savedValue, unsavedValue, operator)
        })

        datasetMap[returnId].values = values
        datasetMap[returnId].times = allTimes
      }
    }
  })

  return Object.values(datasetMap)
}

function getDatasetsByReturnId(datasets: Dataset[]): Record<string, Dataset> {
  const datasetsByReturnId: Record<string, Dataset> = datasets.reduce((acc, dataset) => {
    return {
      ...acc,
      [dataset.return_id]: dataset,
    }
  }, {})

  return datasetsByReturnId
}

function setAbsoluteValues<T extends Pick<Dataset, 'values'>>(dataset: T): T {
  const values = clone(dataset.values)
  return {
    ...clone(dataset),
    values: values.map((value) => (value ? Math.abs(value) : value)),
  }
}

function getAggregatedDatasetsByReturnId(
  datasets: Dataset[],
  aggregationMethod: DatasetInstructionAggregate
): Record<string, Dataset> {
  const datasetsByReturnId: Record<string, Dataset> = datasets.reduce((acc, dataset) => {
    return {
      ...acc,
      [dataset.return_id]: aggregateDataset(clone(dataset), aggregationMethod || `sum`),
    }
  }, {})
  return datasetsByReturnId
}

function getDatasetWithShortestValueLengthInDatasetInstruction(
  datasets: Dataset[],
  datasetInstruction: CalcDatasetInstruction
): Dataset {
  const contract = datasetInstruction.contract as DatasetContractCalc
  const variables = new Set(contract.variables)
  const variablesDatasets = datasets.filter((dataset) => variables.has(dataset.return_id))
  let shortest = variablesDatasets[0]

  variablesDatasets.forEach((dataset) => {
    if (dataset.values.length < shortest.values.length) {
      shortest = dataset
    }
  })

  return shortest
}

function applyOperator(a: number, b: number, operator: string): number {
  switch (operator) {
    case '+':
      return a + b
    case '-':
      return a - b
    case '*':
      return a * b
    case '/':
      if (b === 0) {
        throw new Error('Division by zero')
      }
      return a / b
    default:
      throw new Error('Invalid operator')
  }
}

export function isCalcDatasetInstruction(
  datasetInstruction: DatasetInstruction
): datasetInstruction is CalcDatasetInstruction {
  return datasetInstruction.type === `calc`
}
